/**
 * Token Refresh Middleware for OpenAPI Client
 * 
 * Middleware that automatically refreshes Firebase tokens when API requests
 * return 401 errors and retries the request with the fresh token.
 */

import type { Middleware, ResponseContext } from '@/api/generated/client/runtime';
import { getFreshToken } from './token-refresh';

/**
 * Create a middleware that handles token refresh on 401 errors
 * 
 * @param setTokenFn - Function to update the API client's token
 * @returns Middleware instance
 */
export function createTokenRefreshMiddleware(
  setTokenFn: (token: string) => void
): Middleware {
  return {
    post: async (context: ResponseContext): Promise<Response | void> => {
      const { response } = context;
      
      // If response is 401 Unauthorized, refresh token and retry
      if (response.status === 401) {
        console.log('[Token Refresh Middleware] Detected 401, refreshing token...');
        
        try {
          // Get a fresh token (force refresh)
          const freshToken = await getFreshToken(true);
          
          // Update the API client with the new token
          setTokenFn(freshToken);
          
          console.log('[Token Refresh Middleware] Token refreshed, retrying request...');
          
          // Retry the request with the new token
          const retryResponse = await fetch(context.url, {
            ...context.init,
            headers: {
              ...context.init.headers,
              'Authorization': `Bearer ${freshToken}`,
            },
          });
          
          return retryResponse;
        } catch (error) {
          console.error('[Token Refresh Middleware] Failed to refresh token:', error);
          // Return the original 401 response if refresh fails
          return response;
        }
      }
      
      // For non-401 responses, return undefined to use original response
      return undefined;
    },
  };
}
